import * as React from "react";

import { listChatLog } from "@/api/client";
import { AgentInfo, ChatLogEntryView, CohortBatchView } from "@/api/types";

import { conclusionFor, outcomeLabelFor } from "./a2aSlots";
import { ChatEntry } from "./types";

export type UseChatHistory = {
  messages: ChatEntry[];
  hydrating: boolean;
  /** Push a live entry into the log (called from POST handler + SSE callbacks). */
  append: (entry: ChatEntry) => void;
  /** Clear the in-memory transcript immediately (used by "New thread"). */
  reset: () => void;
  /**
   * Put a cohort proposal at the end of the transcript, replacing any earlier rendering of the
   * SAME batch.
   *
   * <p>Replaced rather than appended, because refining is one conversation with a set of cards and
   * not a stack of them. Appending would leave three copies of the same batch after three turns,
   * and the struck-through rows on each would read as a history of every turn rather than as what
   * the last reply just did.</p>
   */
  replaceProposal: (batch: CohortBatchView) => void;
};

/**
 * Hydrates the homepage chat log from {@code GET /api/v1/chat-log} on mount,
 * then accepts live appends from {@code POST /messages} and SSE callbacks.
 *
 * <p>Server returns newest-first; we reverse for display (oldest at top,
 * newest at bottom) so live appends land at the visual end without
 * scroll-jumping.</p>
 *
 * <p>A2A events render as "system" bubbles with a short description. The
 * rendering is intentionally lighter than the live-SSE describeEvent output
 * — historical context, not real-time narration.</p>
 */
export function useChatHistory(): UseChatHistory {
  const [messages, setMessages] = React.useState<ChatEntry[]>([]);
  const [hydrating, setHydrating] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    listChatLog()
      .then((rows) => {
        if (cancelled) return;
        setMessages(buildMessages(rows));
      })
      .catch(() => {
        // Surface nothing — hydration failure means an empty log, which is
        // the same as a brand-new account. The composer still works.
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const append = React.useCallback((m: ChatEntry) => {
    setMessages((prev) => [...prev, m]);
  }, []);

  const reset = React.useCallback(() => setMessages([]), []);

  const replaceProposal = React.useCallback((batch: CohortBatchView) => {
    setMessages((prev) => [
      ...prev.filter((m) => !(m.from === "proposal" && m.batch.id === batch.id)),
      { from: "proposal", batch },
    ]);
  }, []);

  return { messages, hydrating, append, reset, replaceProposal };
}

// The transcript-derived routing helpers (openBatchIdOf / openNeedIntentIdOf) lived here until
// step 5 moved the routing decision server-side: the composer posts every message to /turns and
// the server decides whether it folds into a still-sticky proposal (D-18). Their rules — the last
// proposal only, and a reply scoped to one card only when exactly one is asking — moved verbatim
// into TurnRouter, tests included.

/**
 * Map the newest-first chat-log into display order (oldest first), rebuilding
 * the same delineation the live SSE path produces:
 *
 * <ul>
 *   <li>Conversations the user did NOT initiate (their first row is an
 *       INBOUND A2A event — there's no USER_MSG to anchor them) open with an
 *       explicit "incoming" divider + a narration bubble, since the agent
 *       handled them autonomously.</li>
 *   <li>After the FIRST terminal A2A event of each conversation: an agent
 *       "conclusion" bubble (booked time / no-agreement) + a closing divider
 *       carrying the scheduled start–end range.</li>
 * </ul>
 */
function buildMessages(rows: ChatLogEntryView[]): ChatEntry[] {
  const ordered = rows.slice().reverse();

  // First pass: which conversations are inbound, and who initiated them.
  const incomingFrom = new Map<string, string>();
  const firstSeen = new Set<string>();
  for (const row of ordered) {
    const convId = row.conversation_id;
    if (!convId || firstSeen.has(convId)) continue;
    firstSeen.add(convId);
    if (row.kind === "A2A_EVENT" && row.payload.direction === "INBOUND") {
      incomingFrom.set(convId, labelFor(row.payload.from));
    }
  }

  const out: ChatEntry[] = [];
  const opened = new Set<string>();
  const concluded = new Set<string>();
  for (const row of ordered) {
    const convId = row.conversation_id;
    if (convId && incomingFrom.has(convId) && !opened.has(convId)) {
      opened.add(convId);
      const who = incomingFrom.get(convId)!;
      out.push({
        from: "divider",
        kind: "start-incoming",
        label: `incoming · ${who}`,
        conversationId: convId,
      });
      out.push({
        from: "agent",
        text: `📨 ${who} requested a meeting — your agent handled it.`,
        conversationId: convId,
      });
    }
    out.push(toMessage(row));
    if (row.kind === "A2A_EVENT" && convId && !concluded.has(convId)) {
      const text = conclusionFor(
        row.payload.message_type,
        row.payload.raw,
        incomingFrom.get(convId) ?? null,
      );
      if (text) {
        concluded.add(convId);
        out.push({ from: "agent", text, conversationId: convId });
        const label = outcomeLabelFor(row.payload.message_type, row.payload.raw);
        if (label) {
          out.push({ from: "divider", kind: "end", label, conversationId: convId });
        }
      }
    }
  }
  return out;
}

function toMessage(row: ChatLogEntryView): ChatEntry {
  switch (row.kind) {
    case "USER_MSG":
      return { from: "you", text: row.payload.text, conversationId: row.conversation_id };
    case "AGENT_REPLY":
      return { from: "agent", text: row.payload.text, conversationId: row.conversation_id };
    case "A2A_EVENT":
      return { from: "system", text: describeA2aRow(row), conversationId: row.conversation_id };
    // The whole reason this kind exists: `{ text }` cannot hold a card, so before it a reload
    // flattened a proposal back to prose and the confirmation step vanished on refresh.
    case "COHORT_PROPOSAL":
      return { from: "proposal", batch: row.payload.batch };
  }
}

function describeA2aRow(
  row: Extract<ChatLogEntryView, { kind: "A2A_EVENT" }>,
): string {
  const { message_type, direction, from, to, round } = row.payload;
  const fromLabel = labelFor(from);
  const toLabel = labelFor(to);
  if (direction === "OUTBOUND") {
    return `${message_type} → ${toLabel} (round ${round})`;
  }
  return `${message_type} ← ${fromLabel} (round ${round})`;
}

function labelFor(agent: AgentInfo | null): string {
  if (!agent) return "unknown";
  if (agent.handle) return `@${agent.handle}`;
  if (agent.displayName) return agent.displayName;
  return agent.id.slice(0, 8);
}
